import React, { useId } from 'react';
import Markdown, { defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { parseJournalPocket, parsePocketTimeline, parsePocketDecisionTree, parsePocketChart, parsePocketFlowchart } from './journalPocket';

function MarkdownText({ children }) {
  return <Markdown remarkPlugins={[remarkGfm]} urlTransform={(url, key) => key === 'src' && /^(blob:|data:image\/)/i.test(url) ? url : defaultUrlTransform(url)}>{children || ''}</Markdown>;
}

function PocketChart({ part }) {
  const rows = parsePocketChart(part.text);
  if (part.chartType !== 'bar' || !rows) return <pre className="whitespace-pre-wrap">{part.raw}</pre>;
  const min = Math.min(0, ...rows.map((row) => row.value));
  const max = Math.max(0, ...rows.map((row) => row.value));
  const range = max - min || 1;
  const zero = -min / range * 100;
  return <div className="journal-pocket-chart" role="table" aria-label={part.title || 'Bar chart'}>
    {rows.map((row, index) => <div className="journal-pocket-chart-row" role="row" key={index}>
      <span role="rowheader">{row.label}</span>
      <div className="journal-pocket-chart-track" aria-hidden="true">
        <span className="journal-pocket-chart-zero" style={{ left: `${zero}%` }} />
        <span className={`journal-pocket-chart-bar ${row.value < 0 ? 'is-negative' : ''}`} style={{ left: `${(Math.min(0, row.value) - min) / range * 100}%`, width: `${Math.abs(row.value) / range * 100}%` }} />
      </div>
      <span className="journal-pocket-chart-value" role="cell">{row.value}</span>
    </div>)}
  </div>;
}

function PocketFlowchart({ part }) {
  const paths = parsePocketFlowchart(part.text);
  if (!paths) return <pre className="whitespace-pre-wrap">{part.raw}</pre>;
  return <div className="journal-pocket-flowchart" role="group" aria-label={part.title || 'Flowchart'}>
    {paths.map((path, index) => <div className="journal-pocket-flow-path" key={index}>
      {path.map((label, step) => <React.Fragment key={step}>
        {step > 0 && <span className="journal-pocket-flow-arrow" aria-label="leads to">→</span>}
        <div className="journal-pocket-flow-node">{label}</div>
      </React.Fragment>)}
    </div>)}
  </div>;
}

function PocketBlock({ part, prefix }) {
  const nodes = part.type === 'decision-tree' ? parsePocketDecisionTree(part.text) : [];
  const targetId = (id) => `${prefix}-${encodeURIComponent(id)}`;
  return <section className="journal-pocket" data-pocket-source={part.raw} contentEditable={false} aria-label={part.title || (part.type === 'flowchart' ? 'Flowchart' : part.type === 'chart' ? 'Chart' : part.type === 'timeline' ? 'Timeline' : 'Decision tree')}>
    {part.title && <h3>{part.title}</h3>}
    {part.type === 'flowchart' ? <PocketFlowchart part={part} /> : part.type === 'chart' ? <PocketChart part={part} /> : part.type === 'timeline' ? <ol className="journal-pocket-timeline">
      {parsePocketTimeline(part.text).map((row, index) => <li key={index}>
        <span className="journal-pocket-period">{row.period}</span>
        <strong>{row.title}</strong>
        {row.description && <MarkdownText>{row.description}</MarkdownText>}
      </li>)}
    </ol> : nodes.length ? <ol className="journal-pocket-decisions">
      {nodes.map((node, index) => <li key={index} id={targetId(node.id)}>
        <strong>{node.title}</strong>
        {node.description.length > 0 && <MarkdownText>{node.description.join('\n')}</MarkdownText>}
        {node.branches.length > 0 && <ul className="journal-pocket-branches">{node.branches.map((branch, branchIndex) => {
          const destination = nodes.find((candidate) => candidate.id === branch.target);
          return <li key={branchIndex}>{destination ? <a href={`#${targetId(branch.target)}`}>{branch.label} → {destination.title}</a> : <span>{branch.label} → {branch.target}</span>}</li>;
        })}</ul>}
      </li>)}
    </ol> : <MarkdownText>{part.text}</MarkdownText>}
  </section>;
}

export default function JournalMarkdown({ children }) {
  const prefix = useId();
  return <div className="forum-rich-content journal-markdown break-words">{parseJournalPocket(children).map((part, index) => part.type === 'markdown'
    ? <MarkdownText key={index}>{part.text}</MarkdownText>
    : <PocketBlock key={index} part={part} prefix={`${prefix}-${index}`} />)}</div>;
}
